import type { ReactElement, ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

/**
 * "Are you sure?", asked properly.
 *
 * Three rules, and they are the difference between a dialog that protects
 * somebody and one they learn to click through:
 *
 * - **The confirm button says what it does** — "Sign out", "Delete BA 2 CHA
 *   1234" — never "OK". Somebody skim-reading sees the verb, not a shrug.
 * - **Cancel is the safe default.** The native `<dialog>` closes on Escape and
 *   on a backdrop click, and both of those land on cancel.
 * - **It is only used where undoing is real work.** A confirmation on
 *   something reversible is noise, and noise is what teaches people to press
 *   the button without reading it.
 */

interface ConfirmProps {
  readonly open: boolean;
  readonly title: string;
  /** The consequence, in a sentence. Not a restatement of the title. */
  readonly detail: ReactNode;
  readonly confirmLabel: string;
  /** `alert` for anything that destroys something. */
  readonly tone?: 'primary' | 'alert';
  readonly busy?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function Confirm({
  open,
  title,
  detail,
  confirmLabel,
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmProps): ReactElement {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button
            intent={tone === 'alert' ? 'danger' : 'primary'}
            size="touch"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working' : confirmLabel}
          </Button>
          <Button size="touch" onClick={onCancel}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="text-body">{detail}</div>
    </Modal>
  );
}
